import { Observable, of, Subject } from 'rxjs';
import { AjaxError } from 'rxjs/ajax';
import { catchError, map } from 'rxjs/operators';
import { Net } from '../data/net';
import { NodeConnection, NodeRequest, NodeRequestOptions } from '../data/node-connection';
import { PowerShellCommand } from '../data/powershell';
import { LogLevel } from '../diagnostics/log-level';
import { Logging } from '../diagnostics/logging';
import { SmeWebTelemetry } from '../diagnostics/sme-web-telemetry';
import { TelemetryEventStates } from '../diagnostics/sme-web-telemetry-models';
import { Strings } from '../generated/strings';
import { EnvironmentModule } from '../manifest/environment-modules';
import { NotificationMessage } from './notification-message';
import { NotificationState } from './notification-state';
import { SocketMessage, SocketMessageFlags, SocketSignalR } from './socket-signalr';
import { WorkItemResult } from './work-item-request';

/**
 * PowerShell Submit result object.
 */
export interface PowerShellSubmitResult {
    id: string;
    completed: boolean;
    error: any;
    state: NotificationState;
}

/**
 * Powershell work item message.
 */
export interface PowerShellWorkItemMessage {
    sessionId?: string;
    completed?: string; // True, False
    results?: any[];
    progress?: any[];
    errors?: any[];
    exception?: string;
}

/**
 * The PowerShell script based notification class.
 */
export class PowerShellNotification extends SocketSignalR<PowerShellWorkItemMessage | NotificationMessage> {
    private static connectionUrl = '/api/notifications';
    private static proxyName = 'notificationHub';
    private static notify = 'Notify';
    private static subscribe = 'Subscribe';
    private subjectInternal: Subject<SocketMessage<PowerShellWorkItemMessage | NotificationMessage>>;

    /* tslint:disable:no-bitwise */
    public static hasConnectionError(item: SocketMessage<PowerShellWorkItemMessage | NotificationMessage>): boolean {
        return (item.type & SocketMessageFlags.ConnectionError) === SocketMessageFlags.ConnectionError;
    }

    public static hasError(item: SocketMessage<PowerShellWorkItemMessage | NotificationMessage>): boolean {
        return (item.type & SocketMessageFlags.Error) === SocketMessageFlags.Error;
    }

    public static hasException(item: SocketMessage<PowerShellWorkItemMessage | NotificationMessage>): boolean {
        return (item.type & SocketMessageFlags.Exception) === SocketMessageFlags.Exception;
    }

    public static hasCompleted(item: SocketMessage<PowerShellWorkItemMessage| NotificationMessage>): boolean {
        return (item.type & SocketMessageFlags.Completed) === SocketMessageFlags.Completed;
    }

    public static hasData(item: SocketMessage<PowerShellWorkItemMessage| NotificationMessage>): boolean {
        return (item.type & SocketMessageFlags.Data) === SocketMessageFlags.Data;
    }

    public static hasProgress(item: SocketMessage<PowerShellWorkItemMessage | NotificationMessage>): boolean {
        return (item.type & SocketMessageFlags.Progress) === SocketMessageFlags.Progress;
    }

    /**
     * Initializes a new instance of the GatewaySocket class.
     */
    constructor(gatewayUrl: string) {
        super(gatewayUrl, PowerShellNotification.connectionUrl, PowerShellNotification.proxyName, !EnvironmentModule.isGatewayV200);
    }

    /**
     * Gets the subject observable to get notifications.
     */
    public get subject(): Observable<SocketMessage<PowerShellWorkItemMessage | NotificationMessage>> {
        return this.subjectInternal.asObservable();
    }

    /**
     * Submit PowerShell command as a work item.
     *
     * @param nodeConnection The node connection.
     * @param command The PowerShell command.
     * @param metadata The meta data.
     * @param nodeRequestOptions The node request options.
     * @param create The callback to create new notification.
     * @return Observable the observable of initial powershell query result.
     */
    public submit(
        nodeConnection: NodeConnection,
        nodeName: string,
        command: PowerShellCommand,
        metadata: any,
        nodeRequestOptions?: NodeRequestOptions,
        create?: (result: PowerShellSubmitResult) => void): Observable<WorkItemResult> {
        // For a non-powershell long running task (eg: azure site recovery setup):
        // The 'command' parameter is null.
        // The metadata.id is the workitem id generated by a gateway api.
        if (!command && metadata.id) {
            const result = <PowerShellSubmitResult>{
                id: metadata.id,
                completed: false,
                error: null,
                state: NotificationState.Started
            };
            if (create) {
                create(result);
            }

            this.subscribeSession(metadata.id);
            return of(<WorkItemResult>{ id: metadata.id, state: NotificationState.Started, error: null });
        }

        const data: any = Net.createPropertiesJSONString({
            ...command,
            useInProcRunspace: nodeRequestOptions.useInProcRunspace,
            ...{ metadata: metadata } });
        return nodeConnection.post(nodeName, Net.powerShellApiInvokeCommand, data, <NodeRequest>nodeRequestOptions)
            .pipe(
                catchError((error: AjaxError) => {
                    SmeWebTelemetry.tracePowershellEvent(command, TelemetryEventStates.Error,
                        {response: error.response});

                    const result = <PowerShellSubmitResult>{
                        id: MsftSme.newGuid(),
                        completed: true,
                        error: { message: Net.getErrorMessage(error) },
                        state: NotificationState.Error
                    };
                    if (create) {
                        create(result);
                    }

                    return of(<WorkItemResult>{ id: result.id, state: result.state, error: result.error });
                }),
                map((response: any) => {
                    const completed = response.completed === 'True';

                    if (response.id) {
                        // sending back the client for the error.
                        return <WorkItemResult>response;
                    }

                    let error: any = null;
                    if (response.errors && response.errors.length > 0) {
                        const errorObj: any = MsftSme.first(response.errors);
                        error = { message: errorObj.message };

                        SmeWebTelemetry.tracePowershellEvent(command,
                            TelemetryEventStates.Error,
                            {response: response});
                    }

                    const result = <PowerShellSubmitResult>{
                        id: response.sessionId,
                        completed: completed,
                        error: error,
                        state: completed ?
                            (response.errors ? NotificationState.Error : NotificationState.Success)
                            : NotificationState.Started
                    };

                    if (result.state === NotificationState.Success) {
                        result.error = response.results[0];
                    }

                    if (create) {
                        create(result);
                    }

                    if (!result.completed) {
                        this.subscribeSession(response.sessionId);

                        // store ID here to correlate command with the gateway notification later
                        const powershellCommandId = response.id ? response.id : response.sessionId;
                        SmeWebTelemetry.addPowershellId(powershellCommandId, command);
                    }

                    this.processPowerShellData(response);

                    // sending back the client.
                    const workItemResult = <WorkItemResult>{ id: result.id, state: result.state, error: error };

                    // passing in the results as object of the workItemResult
                    if (response.results) {
                        workItemResult.object = response.results;
                    }

                    return workItemResult;
                }));
    }

    /**
     * Initialize to subscribe the web socket connection.
     */
    public initialize(): Promise<any> | JQueryPromise<any> {
        if (this.subjectInternal) {
            this.subjectInternal.complete();
        }

        this.subjectInternal = new Subject<SocketMessage<PowerShellWorkItemMessage | NotificationMessage>>();
        this.subscribe(PowerShellNotification.notify);
        return this.start();
    }

    /**
     * Uninitialize to subscribe the web socket connection.
     */
    public uninitialize(): void {
        if (this.subjectInternal) {
            this.subjectInternal.complete();
            this.subjectInternal = null;
        }

        this.unsubscribe(PowerShellNotification.notify);
        this.stop();
    }

    /**
     * Invoke subscribe method to a  session.
     *
     * @param id the session ID of work item.
     * @return Promise the promise object.
     */
    public subscribeSession(id: string): Promise<PowerShellWorkItemMessage | NotificationMessage> | JQueryPromise<PowerShellWorkItemMessage | NotificationMessage> {
        return this.invoke(PowerShellNotification.subscribe, id);
    }

    /**
     * The client handler to process message.
     *
     * @param notification the message notification.
     */
    protected clientHandler(notification: SocketMessage<PowerShellWorkItemMessage | NotificationMessage>): void {
        this.subjectInternal.next(notification);
    }

    /**
     * Process the message.
     *
     * @param messages the messages.
     */
    protected processMessage(message: string): void {
        if (!message) {
            const message2 = MsftSme.getStrings<Strings>().MsftSmeShell.Core.Error.NotificationEmptyMessage.message;
            Logging.log({ source: 'socket', level: LogLevel.Warning, message: message2 });
            return;
        }

        const data: any = JSON.parse(message);
        if (data.sessionId) {
            // PowerShell work item.
            this.processPowerShellData(data);
            return;
        }

        if (data.id) {
            // Notification message.
            this.processMessageData(data);
            return;
        }
    }

    private processPowerShellData(data: PowerShellWorkItemMessage): void {
        /* tslint:disable:no-bitwise */
        let type: SocketMessageFlags = SocketMessageFlags.None;
        if (data.results && data.results.length > 0) {
            type |= SocketMessageFlags.Data;
        }

        if (data.progress && data.progress.length > 0) {
            type |= SocketMessageFlags.Progress;
        }

        if (data.errors && data.errors.length > 0) {
            type |= SocketMessageFlags.Error;
        }

        if (data.exception) {
            type |= SocketMessageFlags.Exception;
        }

        if (data.completed === 'True') {
            type |= SocketMessageFlags.Completed;
        }

        this.clientHandler({ type: type, message: data });
    }

    private processMessageData(message: NotificationMessage): void {
        const type: SocketMessageFlags = SocketMessageFlags.Data;
        this.clientHandler({ type, message });
    }
}
